import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { auth, SESSION_EXPIRED_EVENT } from "@/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await auth.me();
      setUser(me);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Sesión expirada en el servidor (401 en cualquier llamada): <Protected> redirige a login.
  useEffect(() => {
    const onExpired = () => setUser(null);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  // Section 6.1: Local login (existing)
  const login = async (email, password) => {
    const data = await auth.login(email, password);
    // Token is now stored in HttpOnly cookie by backend
    setUser(data.user);
    return data.user;
  };

  // Section 6.1: Entra SSO login
  // Redirects to backend OAuth2/OIDC flow endpoint
  const loginWithEntra = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    window.location.href = `${backendUrl}/api/auth/entra/login`;
  };

  // Section 6.5: Logout (supports both local and SSO sessions)
  const logout = async () => {
    try { 
      await auth.logout(); 
    } catch (_) {}
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ 
      user, 
      loading, 
      login, 
      loginWithEntra,  // New: Section 6.1
      logout, 
      refresh 
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
